import { useCallback } from 'react';

import type { SlotPopupSource } from '@sunflower-games/analytics-events';

import { EnumOpenDialog } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';

import type { CrownJackpotWinOptions } from './CrownJackpotWinDialog';

// Winners banner + Daily Missions slot click (MZ-777). Gate: a logged-in player (the logged-out
// landing page mounts no dialogs) and the slot is in one of the lobby's categories, loaded or not.
export function useCanOpenSlotPopup(): (gameId: number) => boolean {
  const { authStore, categoriesStore } = useStores();

  return useCallback(
    (gameId: number) => authStore.authorized && categoriesStore.isGameInAnyCategory(gameId),
    [authStore, categoriesStore],
  );
}

export function useOpenSlotPopup(): (gameId: number, source: SlotPopupSource) => void {
  const { uiStore } = useStores();
  const canOpenSlotPopup = useCanOpenSlotPopup();

  return useCallback(
    (gameId: number, source: SlotPopupSource) => {
      if (!canOpenSlotPopup(gameId)) return;
      uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenSlotPopupDialog, options: { gameId, source } });
    },
    [canOpenSlotPopup, uiStore],
  );
}

// Winners banner Crown Jackpot tile (MZ-829). Unlike the slot pop-up it opens even when the winning slot
// isn't in this player's lobby — the pop-up then sends Play back to the lobby. Logged-in players only.
export function useOpenCrownJackpotWin(): ((win: CrownJackpotWinOptions) => void) | undefined {
  const { authStore, uiStore } = useStores();
  const open = useCallback(
    (win: CrownJackpotWinOptions) => {
      uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenCrownJackpotWinDialog, options: { ...win } });
    },
    [uiStore],
  );

  return authStore.authorized ? open : undefined;
}
